import { useState } from 'react'
import img from './assets/pick-a-side.png'
import { ShopButton } from './Buttons'
import { SectionHeading, SectionSubheading } from './SectionHeading'

const APES_COPY =
    'Originally conceived as science projects aimed at enhancing physical capabilities, Apes evolved beyond their initial roles as manual labourers and workers, making them fascinating and formidable participants in the BR1 Arena.'

const DROIDS_COPY =
    'Government-produced droids, sometimes sentient and remotely controlled, are militarized robots made from rare high-density alloys. Droids are immortal and easily controlled, serving in military, police, and hazardous roles.'

/**
 * The same two factions in a line each, for the phone.
 *
 * The lore above is 232 characters per side. In a mobile column that is thirteen lines, and no
 * arrangement of a picture and thirteen lines of text fits on a 375px screen without one of them
 * looking like an afterthought — which is what two earlier attempts at this section proved. Short
 * enough to sit under a portrait, the two can stand side by side instead, which is also the one
 * layout that says what the section is asking.
 *
 * Desktop still uses the full text; nothing here replaces it.
 */
const APES_SHORT = 'Built in a lab. Forged in the Arena.'
const DROIDS_SHORT = 'Government steel. Built for war.'

/**
 * Where each character sits inside the composite artwork.
 *
 * `width` scales the image against the box, `left` and `top` slide it, and both offsets are
 * percentages of the *box* — so the crop holds at any column width provided the box keeps its 3:4
 * shape. Measured off the source (1917x766) rather than guessed:
 *
 *   ape    x 0.130-0.322  y 0.020-0.661   natural aspect 0.751, so it fills a 3:4 box
 *   droid  x 0.718-0.838  y 0.050-0.680   natural aspect 0.476, fitted by height and centred
 *
 * The droid is a much slimmer figure than the ape, which is why he does not fill his box sideways.
 * Both are still downscaled — 368px and 230px of source into a ~120px column — so neither softens.
 */
type Crop = { width: string; left: string; top: string }

const APE_CROP: Crop = { width: '521%', left: '-67.7%', top: '-3.1%' }
const DROID_CROP: Crop = { width: '530%', left: '-362.1%', top: '-7.9%' }

/**
 * One faction as a card: a framed portrait, then the name and description.
 *
 * Stacked full-width, these two ran to 975px on a phone. Two attempts at compressing it were worse
 * than the length was:
 *
 * Two flex columns left the copy running 100px past the bottom of the portrait, so each row ended
 * in a block of nothing beside the picture. Floating the portrait instead let those last lines
 * reclaim the width, which is shorter still — but the text then wrapped into an L, and mirroring
 * the two rows put one heading beside its character and the other above it. Two rows built the same
 * way that do not look the same way.
 *
 * Side by side is the layout the section is asking for — two options, presented as two options —
 * and it only became available once the copy was short enough to sit under a portrait. With the
 * full lore neither of the alternatives worked: two flex columns left the copy running 100px past
 * the bottom of the picture, and floating the picture fixed that but wrapped the text into an L
 * and put one heading beside its character and the other above it.
 *
 * The frame is not decoration. The crop is a rectangle of the composite, and the composite is not
 * black behind the characters — a soft glow band crosses both of them at about a third height.
 * Unframed, that rectangle showed as a pale box floating on the page. Bordered and lit from the
 * base in the brand colours, it reads as a portrait, which is what it is.
 *
 * The box is 3:4 because that is the shape both characters were measured against, so each fills
 * his own frame at any column width.
 */
const Faction = ({ name, copy, crop }: { name: string; copy: string; crop: Crop }) => (
    <div className='flex flex-col'>
        <div className='relative aspect-[3/4] w-full overflow-hidden border border-white/10 bg-black bg-[radial-gradient(115%_75%_at_50%_105%,rgba(201,31,255,0.22)_0%,rgba(3,206,255,0.10)_50%,transparent_78%)]'>
            <img src={img} alt='' aria-hidden className='absolute max-w-none select-none' style={crop} />
        </div>
        <h2 className='mt-3 font-monument text-lg'>{name}</h2>
        <div className='mt-1.5 h-px w-8 bg-[linear-gradient(94.38deg,#03CEFF_-17.38%,#C91FFF_108.85%)]' />
        <p className='mt-2 text-sm leading-relaxed text-white/70'>{copy}</p>
    </div>
)

/**
 * Reflows on mobile rather than scaling.
 *
 * Every size here was a bare `cqi`, and the two faction columns are a third of the width each
 * (`right-2/3` / `left-2/3`). On a 375px phone that made their container 88px, so the body copy
 * computed to **3px** and the headings to 7px.
 *
 * The first attempt at a mobile layout stacked the whole composite image above both blocks of text,
 * which fixed the sizes but broke the meaning: the descriptions no longer sat with the character
 * they describe. Each faction now carries its own crop of the artwork, which is what the desktop
 * composition says with its left and right columns.
 *
 * From `md` the original overlay layout is used unchanged.
 */
export function PickASide() {
    const [current, setCurrent] = useState(0)

    return (
        <div className='tracking-wide container-inline'>
            {/* ---------- mobile: one row per faction, then the way in ---------- */}
            <div className='md:hidden flex flex-col gap-6 px-4 pt-8'>
                {/* Same heading component as every other section, so this stops being the one that
                    is 24px in Orbitron while the rest are Monument. */}
                <div className='flex flex-col gap-2'>
                    <SectionHeading>PICK A SIDE</SectionHeading>
                    <SectionSubheading>TEAM APE OR TEAM DROID?</SectionSubheading>
                </div>

                <div className='grid grid-cols-2 gap-3'>
                    <Faction name='APES' copy={APES_SHORT} crop={APE_CROP} />
                    <Faction name='DROIDS' copy={DROIDS_SHORT} crop={DROID_CROP} />
                </div>

                {/* The call to action ends the section rather than opening it. Above the factions it
                    asked people to choose before they had been told what the choice was; here it is
                    what the section has been building to. */}
                <ShopButton className='w-full' />
            </div>

            {/* ---------- md and up: the original overlay composition ---------- */}
            <div className='hidden md:block relative mt-[max(calc(2*var(--text-xs)),4cqi)]'>
                <img src={img} className='select-none [-webkit-user-drag:none]' alt='' />
                <div className='absolute left-0 top-0 right-0 font-bold'>
                    {/* The size stays proportional to the artwork it is overlaid on — this is a
                        composition, not a stacked section, and a fixed cap would leave the heading
                        floating in a space built for it. Only the typeface is brought into line
                        with the other sections. */}
                    <div className='flex flex-col items-center pt-[3cqi]'>
                        <h2 className='font-monument text-[3.4cqi]'>PICK A SIDE</h2>
                        <div className='text-[1.7cqi]'>TEAM APE OR TEAM DROID?</div>
                        <div className='pt-[2cqi]'>
                            <ShopButton />
                        </div>
                    </div>
                </div>
                <div
                    className='absolute left-[10%] top-0 bottom-1/4 right-2/3 flex flex-col justify-end container-inline font-bold transition-opacity'
                    style={{ opacity: current === 0 ? 1 : 0.3 }}
                    onMouseEnter={() => setCurrent(0)}
                >
                    <h2 className='font-monument text-[8cqi]'>APES</h2>
                    <div className='text-[3.5cqi]'>{APES_COPY}</div>
                </div>
                <div
                    className='absolute right-[10%] top-0 bottom-1/4 left-2/3 flex flex-col justify-end container-inline font-bold text-right transition-opacity'
                    style={{ opacity: current === 1 ? 1 : 0.3 }}
                    onMouseEnter={() => setCurrent(1)}
                >
                    <h2 className='font-monument text-[8cqi]'>DROIDS</h2>
                    <div className='text-[3.5cqi]'>{DROIDS_COPY}</div>
                </div>
            </div>
        </div>
    )
}
